import {
    createContext,
    Dispatch,
    FC,
    ReactNode,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import { ApplicationFilters } from '../../../../types/ApplicationFilters';
import { DEFAULT_SORT_DIRECTION, DEFAULT_SORT_FIELD } from '../consts';
import { Sorting } from '../../../../types/Sorting';
import { Application } from '../../../../types/Application';
import { useGetApplications } from '../../../../api/queries/useGetApplications';
import { useActiveDirectoryAccessContext } from '../../../../context';
import { DaysRangeToValues } from '../../../../types/DaysRange';

interface ApplicationsContextProperties {
    applications: Array<Application>;
    applicationsLoading: boolean;
    fetchMoreApplications: () => void;
    filters: ApplicationFilters;
    filtersApplied: boolean;
    refetchApplications: () => void;
    setShowUploadApplicationsModal: Dispatch<SetStateAction<boolean>>;
    setSorting: Dispatch<SetStateAction<Sorting>>;
    showUploadApplicationsModal: boolean;
    sorting: Sorting;
    updateFilter: <K extends keyof ApplicationFilters>(field: K, value: ApplicationFilters[K]) => void;
}

const defaultProperties: ApplicationsContextProperties = {
    applications: [],
    applicationsLoading: true,
    fetchMoreApplications: () => {},
    filters: {},
    filtersApplied: false,
    refetchApplications: () => {},
    setShowUploadApplicationsModal: () => {},
    setSorting: () => {},
    showUploadApplicationsModal: false,
    sorting: { direction: DEFAULT_SORT_DIRECTION, field: DEFAULT_SORT_FIELD },
    updateFilter: () => {}
};

export const ApplicationsContext = createContext<ApplicationsContextProperties>(defaultProperties);

export const ApplicationsProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const { selectedStaleResourcesDaysRange } = useActiveDirectoryAccessContext();

    const [filters, setFilters] = useState<ApplicationFilters>({
        lastAccessInDays: DaysRangeToValues[selectedStaleResourcesDaysRange]
    });
    const [sorting, setSorting] = useState<Sorting>({
        direction: DEFAULT_SORT_DIRECTION,
        field: DEFAULT_SORT_FIELD
    });
    const [showUploadApplicationsModal, setShowUploadApplicationsModal] = useState(false);

    const filtersApplied = Object.keys(filters).length > 0;

    const updateFilter = useCallback(<K extends keyof ApplicationFilters>(field: K, value: ApplicationFilters[K]) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    }, []);

    useEffect(() => {
        const days = DaysRangeToValues[selectedStaleResourcesDaysRange];

        updateFilter('lastAccessInDays', days);
    }, [selectedStaleResourcesDaysRange, updateFilter]);

    const { applications, applicationsLoading, fetchMoreApplications, refetchApplications } = useGetApplications(
        filters,
        sorting
    );

    const contextValue = useMemo(
        () => ({
            applications,
            applicationsLoading,
            fetchMoreApplications,
            filters,
            filtersApplied,
            refetchApplications,
            setShowUploadApplicationsModal,
            setSorting,
            showUploadApplicationsModal,
            sorting,
            updateFilter
        }),
        [
            applications,
            applicationsLoading,
            fetchMoreApplications,
            filters,
            filtersApplied,
            refetchApplications,
            setShowUploadApplicationsModal,
            setSorting,
            showUploadApplicationsModal,
            sorting,
            updateFilter
        ]
    );

    return <ApplicationsContext.Provider value={contextValue}>{children}</ApplicationsContext.Provider>;
};

export const useApplicationsContext = (): ApplicationsContextProperties => useContext(ApplicationsContext);
